'use client';
import './desktop.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import { type ConnectionState, type RetryAttemptContext, type RetryAttemptResult, type RetryController } from 'elowen-plugin-shared/retry';
import { createReconnectController } from '../../lib/reconnect';
import { subscribeRevive } from '../../lib/useRevive';
import { ElowenApiError, getConnectionPolicy, loadConnectionPolicy } from '../../lib/elowenClient';
import { AUTH_CLEARED_EVENT, authGeneration, subscribeAuthTransitions } from '../../lib/token';
import { desktopTicketUnavailableSchema } from '../../lib/desktopTicket';
import type RFB from '@novnc/novnc';
import { randomId } from 'elowen-plugin-shared/randomId';
import type { VncConnectionState, VncTicketResult, VncSurfaceProps } from 'elowen-plugin-ui-kit';

/** The live view: one real RFB client on one canvas.
 *
 *  ONE connection, moved between the thumbnail slot and the expanded slot rather than duplicated. The
 *  obvious alternative — a second, view-only connection for the thumbnail — was measured and rejected:
 *  x11vnc encodes and sends the FULL framebuffer per client, with no per-client downscale, so a 300px
 *  thumbnail pays exactly what the big canvas pays. A second viewer took scrolling from 316 kB/s to
 *  523 kB/s, +65% for a picture a third of the width.
 *
 *  Only one of the two surfaces is ever visible — the expanded one is the shared Modal, raised over the
 *  whole document — so moving the node costs a reparent and nothing else. The container is created once and
 *  outlives every slot change, because it is what noVNC owns: remounting it would drop the connection
 *  every time the reader expands or collapses the card. */

/** Quality preserves the crisp-text default. Speed trades detail for responsiveness: level 2 also
 *  requests the guest's 75% framebuffer; other RFB servers simply use their lower JPEG quality.
 *  Keep both settings together so reconnects and runtime changes use the same policy. */
const ENCODING = { quality: { quality: 8, compression: 6 }, speed: { quality: 2, compression: 1 } } as const;

/** A full room does not empty in half a second, so a refused viewer waits properly instead of polling. */
const VIEWER_LIMIT_RETRY_MS = 10_000;

/** noVNC is an ES module; its used surface is declared once in novnc.d.ts. */
async function loadRfb(): Promise<typeof RFB | null> {
  try {
    return (await import('@novnc/novnc')).default;
  } catch (error) { console.error('Desktop RFB module failed to load', error); return null; }
}

export function useVncSurface({ slot, ticket, interactive, enabled, connectionKey, pictureMode = 'quality' }: VncSurfaceProps): {
  state: VncConnectionState;
  connectionState: ConnectionState | null;
  retryConnection(): void;
  /** The framebuffer's shape, so the canvas box and the picture coincide instead of letterboxing. Known
   *  from the ticket, before a single pixel has arrived. */
  aspect: number | null;
} {
  // A state belongs to the socket that reported it. Consumers act on `connected` in the same commit (a
  // requested takeover claims at once), so the render that re-enables the surface or replaces its key must
  // read `connecting`, never the retired socket's last word.
  const [connection, setConnection] = useState<{ key: string | undefined; state: VncConnectionState } | null>(null);
  const state: VncConnectionState = enabled && connection !== null && connection.key === connectionKey ? connection.state : 'connecting';
  const [aspect, setAspect] = useState<number | null>(null);
  const [observation, setObservation] = useState<{ key: string | undefined; value: ConnectionState } | null>(null);
  const connectionState = enabled && observation?.key === connectionKey ? observation?.value ?? null : null;
  const retryRef = useRef<(() => void) | null>(null);
  const retryConnection = useCallback(() => retryRef.current?.(), []);
  const ownerId = useRef(randomId());
  const containerRef = useRef<HTMLDivElement | null>(null);
  const rfbRef = useRef<RFB | null>(null);
  // Both are read at the moment they matter rather than depended on. `ticket` is a fresh closure every
  // render by design — it has to mint a NEW ticket each time — so depending on it would tear the
  // connection down and rebuild it on every parent render.
  const ticketRef = useRef(ticket);
  ticketRef.current = ticket;
  const interactiveRef = useRef(interactive);
  interactiveRef.current = interactive;
  const pictureModeRef = useRef(pictureMode);
  pictureModeRef.current = pictureMode;

  if (!containerRef.current && typeof document !== 'undefined') {
    const node = document.createElement('div');
    node.className = 'elowen-desktop-surface';
    containerRef.current = node;
  }

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !slot) return;
    slot.appendChild(container);
    return () => { if (container.parentNode === slot) slot.removeChild(container); };
  }, [slot]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) return;
    const lifetime = new AbortController();
    const issuedIn = authGeneration();
    const authority = { kind: 'local-owner', id: randomId() } as const;
    const current = () => !lifetime.signal.aborted && issuedIn === authGeneration();
    const cancel = () => {
      lifetime.abort();
      setConnection(null);
      setObservation(null);
    };
    const offAuth = subscribeAuthTransitions(cancel);
    window.addEventListener(AUTH_CLEARED_EVENT, cancel);
    let reconnect: RetryController<void> | undefined;
    let exhausted = false;
    const report = (next: VncConnectionState): void => {
      if (current()) setConnection({ key: connectionKey, state: next });
    };
    const reportFailure = (error: unknown): void => {
      if (!current()) return;
      console.error('Desktop connection failed', error);
      report('failed');
      setObservation(value => value ? { ...value, value: { ...value.value, freshness: 'stale' } } : null);
    };
    const attempt = async (context: RetryAttemptContext): Promise<RetryAttemptResult<void>> => {
      if (!current() || context.signal.aborted) return { outcome: 'cancelled', reason: 'aborted' };
      report('connecting');
      let issued: VncTicketResult;
      try { issued = await ticketRef.current(context.signal); }
      catch (error) {
        if (!current() || context.signal.aborted) return { outcome: 'cancelled', reason: 'aborted' };
        if (error instanceof TypeError) return { outcome: 'retry', reason: 'network' };
        if (error instanceof ElowenApiError) {
          if (error.status === 401) return { outcome: 'blocked', reason: 'auth_required' };
          if (error.status === 403) return { outcome: 'blocked', reason: 'forbidden' };
          if (error.status === 503 && desktopTicketUnavailableSchema.safeParse(error.details).success) {
            return { outcome: 'retry', reason: 'not_ready', retryAfterMs: error.retryAfterMs };
          }
          return { outcome: 'blocked', reason: 'unavailable' };
        }
        throw error;
      }
      if (!current() || context.signal.aborted) return { outcome: 'cancelled', reason: 'aborted' };
      if (issued.kind === 'refused') {
        report(issued.reason === 'viewer_limit' ? 'viewer_limit' : 'unavailable');
        return issued.reason === 'viewer_limit'
          ? { outcome: 'retry', reason: 'rate_limited', retryAfterMs: Math.max(VIEWER_LIMIT_RETRY_MS, issued.retryAfterMs ?? 0) }
          : { outcome: 'blocked', reason: 'unavailable' };
      }
      const Rfb = await loadRfb();
      if (!current() || context.signal.aborted) return { outcome: 'cancelled', reason: 'aborted' };
      if (!Rfb) { report('failed'); return { outcome: 'blocked', reason: 'unavailable' }; }
      if (issued.expiresAt <= Date.now()) return { outcome: 'retry', reason: 'timeout' };
      if (issued.width && issued.height) setAspect(issued.width / issued.height);
      const url = new URL(issued.url, window.location.origin);
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      const connection = new Rfb(container, url.toString(), { credentials: issued.credentials });
      rfbRef.current = connection;
      connection.scaleViewport = true;
      connection.clipViewport = false;
      connection.qualityLevel = ENCODING[pictureModeRef.current].quality;
      connection.compressionLevel = ENCODING[pictureModeRef.current].compression;
      connection.viewOnly = !interactiveRef.current;
      let active = true;
      let handshake: ReturnType<typeof setTimeout> | undefined;
      const owns = () => active && current() && !context.signal.aborted && rfbRef.current === connection;
      let abort!: () => void;
      try {
        return await new Promise<RetryAttemptResult<void>>(resolve => {
          const finish = (result: RetryAttemptResult<void>) => {
            if (!active) return;
            active = false;
            clearTimeout(handshake);
            if (rfbRef.current === connection) rfbRef.current = null;
            container.removeAttribute('data-keyboard-claimed');
            connection.disconnect();
            resolve(result);
          };
          abort = () => finish({ outcome: 'cancelled', reason: 'aborted' });
          context.signal.addEventListener('abort', abort, { once: true });
          // A ticket's admission window bounds the RFB acknowledgement, not the established viewer.
          handshake = setTimeout(() => finish({ outcome: 'retry', reason: 'timeout' }), issued.expiresAt - Date.now());
          connection.addEventListener('connect', () => {
            if (!owns()) return;
            if (Date.now() >= issued.expiresAt) { finish({ outcome: 'retry', reason: 'timeout' }); return; }
            clearTimeout(handshake);
            report('connected');
            context.ready();
          });
          connection.addEventListener('disconnect', () => {
            if (!owns()) return;
            report('disconnected');
            finish({ outcome: 'retry', reason: 'closed' });
          });
          connection.addEventListener('securityfailure', () => {
            if (!owns()) return;
            report('failed');
            finish({ outcome: 'blocked', reason: 'forbidden' });
          });
        });
      } finally {
        clearTimeout(handshake);
        context.signal.removeEventListener('abort', abort);
        if (active) {
          active = false;
          if (rfbRef.current === connection) rfbRef.current = null;
          container.removeAttribute('data-keyboard-claimed');
          connection.disconnect();
        }
      }
    };

    let starting = false;
    const start = async (restart: boolean): Promise<void> => {
      if (!current() || starting) return;
      starting = true;
      try {
        await loadConnectionPolicy(lifetime.signal);
        if (!current()) return;
        reconnect ??= createReconnectController<void>({
          target: { kind: 'desktop-viewer', id: connectionKey ?? ownerId.current },
          getPolicy: getConnectionPolicy, signal: lifetime.signal, attempt,
          onProgress: progress => {
            if (!current()) return;
            exhausted = progress.phase === 'exhausted';
            const ready = progress.phase === 'succeeded';
            setObservation({ key: connectionKey, value: {
              target: progress.target, generation: progress.generation, sequence: progress.sequence,
              transport: ready ? 'connected' : progress.phase === 'attempting' ? 'connecting' : 'disconnected',
              readiness: ready ? 'ready' : 'pending', retry: progress, observedAt: Date.now(), authority, freshness: 'fresh',
            } });
          },
        });
        await (restart ? reconnect.restart() : reconnect.start()).done;
      } finally { starting = false; }
    };
    retryRef.current = () => { void start(true).catch(reportFailure); };
    const offRevive = subscribeRevive(({ foregroundReturned }) => {
      if (foregroundReturned && exhausted) retryRef.current?.();
    });
    void start(false).catch(reportFailure);
    return () => {
      cancel();
      offAuth();
      offRevive();
      window.removeEventListener(AUTH_CLEARED_EVENT, cancel);
      retryRef.current = null;
      void reconnect?.stop();
    };
  }, [enabled, connectionKey]);

  useEffect(() => {
    const client = rfbRef.current;
    // The one fact a host overlay needs: this surface is feeding the keyboard to a remote machine, so
    // Escape (which would dismiss the dialog around it) belongs to that machine. See `DialogContent`.
    containerRef.current?.toggleAttribute('data-keyboard-claimed', !!client && interactive);
    if (!client) return;
    client.viewOnly = !interactive;
    if (interactive) client.focus();
    else client.blur();
  }, [interactive, state]);

  useEffect(() => {
    const client = rfbRef.current;
    if (!client) return;
    client.qualityLevel = ENCODING[pictureMode].quality;
    client.compressionLevel = ENCODING[pictureMode].compression;
  }, [pictureMode, state]);

  return { state, aspect, connectionState, retryConnection };
}